import { useMemo, useState } from 'react';
import { useCart, useCartActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { ProductImage } from '../../ui/ProductImage';
import { AddButton } from '../shared/AddButton';
import { founder } from '../../data/content';
import { euro } from '../../core/format/currency';
import { cartRecommendations } from './cartRules';

/** Aanbevelingen onder de mand: passend bij wat erin zit, of precies genoeg voor het volgende cadeau. */
export function CartRecommendations() {
  const {
    lines,
    nextGift,
    giftSubtotal
  } = useCart();
  const {
    products,
    productsByIds
  } = useCatalog();
  const missing = nextGift ? nextGift.threshold - giftSubtotal : null;
  const recommendations = useMemo(() => cartRecommendations({
    cartProducts: lines.map(l => l.product),
    missing,
    products,
    productsByIds
  }), [lines, missing, products, productsByIds]);
  if (recommendations.length === 0) return null;
  const title = nextGift ? `Hiermee krijg je ook ${nextGift.phrase}` : `${founder.firstName} adviseert hierbij`;
  return <section className="mt-6 border-t border-line pt-5" aria-labelledby="cart-recs" data-id="element-6692" data-mp="hvy8c1lcign5">
      <h3 id="cart-recs" className="text-[14px] text-ink" data-id="element-6693" data-mp="lxt8h2rjinzc">
        {title}
      </h3>
      <ul className="mt-3 divide-y divide-line" data-id="element-6694" data-mp="lvoyq46ymur5">
        {recommendations.map(p => {
        // Haalt dit product precies het volgende cadeau binnen? Zeg het bij het product zelf.
        const unlocks = missing !== null && missing > 0 && p.price >= missing;
        return <li key={p.id} className="flex items-center gap-3 py-3 first:pt-0" data-id="element-6695" data-mp="10kuv7keey6vc">
              {/* Decoratief: de naam staat ernaast. */}
              <ProductImage src={p.image} alt="" className="h-14 w-12 shrink-0 bg-blush p-1" data-id="element-6696" data-mp="19n41fy2nfe2x" />
              <div className="min-w-0 flex-1" data-id="element-6697" data-mp="37df2fszr8lqo">
                <p className="text-[14px] leading-snug text-ink" data-id="element-6698" data-mp="37eq67st79ths">{p.name}</p>
                <p className="text-[13px] tabular-nums text-muted" data-id="element-6699" data-mp="3bfs8kiieylnz">
                  {euro(p.price)}
                  {p.step && ` · ${p.step.toLowerCase()}`}
                </p>
                {unlocks && nextGift && <p className="mt-1 w-fit rounded-full bg-blush px-2.5 py-0.5 text-[12px] text-ink" data-id="element-6700" data-mp="dm7b6tatih32">+ {nextGift.phrase}</p>}
              </div>
              {/* De mand staat open: geen melding, wel een vinkje op de knop. */}
              <AddButton product={p} silent data-id="element-6701" data-mp="ni1y40m2qybn" />
            </li>;
      })}
      </ul>
    </section>;
}

/** Een code van Sarah of een partner invullen, of de toegepaste code weghalen. */
export function CartCode() {
  const {
    appliedCode
  } = useCart();
  const {
    applyCode,
    removeCode
  } = useCartActions();
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [showCode, setShowCode] = useState(false);
  return <div className="mt-6 border-t border-line pt-5" data-id="element-6702" data-mp="1k8ue6y90cqdf">
      {appliedCode ? <div className="bg-blush px-4 py-3 text-[13px] text-ink" data-id="element-6703" data-mp="1fasv4dbl0r1y">
          <p className="flex items-center justify-between gap-3" data-id="element-6704" data-mp="1f8g9nq6m7yk5">
            <span data-id="element-6705" data-mp="1yxmco9me470o">
              {appliedCode.code} — {appliedCode.label}
            </span>
            <button type="button" onClick={removeCode} className="-my-2 px-2 py-2 text-[13px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6706" data-mp="23ucq0p6t8oie">
              Verwijderen<span className="sr-only" data-id="element-6707" data-mp="1tvn9p0ukbc49">: code {appliedCode.code}</span>
            </button>
          </p>
          <p className="mt-1 text-[13px] text-muted" data-id="element-6708" data-mp="1g48smzk7tkzu">{appliedCode.gift}</p>
        </div> : !showCode ? <button type="button" onClick={() => setShowCode(true)} className="text-[14px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6709" data-mp="1kxuwq3cbcgk1">
          Heb je een code van {founder.firstName} of een partner?
        </button> : <form onSubmit={e => {
      e.preventDefault();
      const ok = applyCode(code);
      setCodeError(!ok);
      if (ok) setCode('');
    }} className="flex gap-2" data-id="element-6710" data-mp="1262acrrr1tla">
          <label htmlFor="cart-code" className="sr-only" data-id="element-6711" data-mp="p5zynk5m76gh">
            Code
          </label>
          <input id="cart-code"
      // Net op "Heb je een code?" gedrukt: dan wil ze typen.
      autoFocus value={code} onChange={e => {
        setCode(e.target.value);
        setCodeError(false);
      }} placeholder="Bijv. SALON" aria-invalid={codeError || undefined} aria-describedby={codeError ? 'cart-code-error' : undefined} autoCapitalize="characters" autoComplete="off" className="w-full border-b border-line bg-transparent px-1 py-2.5 text-[16px] text-ink sm:text-[14px] placeholder:text-muted focus:border-ink focus:outline-none" data-id="element-6712" data-mp="kermigcwh6zm" />
          <button type="submit" className="whitespace-nowrap rounded-full border border-line px-4 text-[13px] text-ink transition-colors duration-150 ease-soft hover:border-ink" data-id="element-6713" data-mp="1rsl67xonxik0">
            Toepassen
          </button>
        </form>}
      {codeError && <p id="cart-code-error" className="mt-2 text-[13px] text-clay" role="alert" data-id="element-6714" data-mp="3enhszu9lz4ci">
          Deze code kennen we niet. Check de spelling, of app {founder.firstName}.
        </p>}
    </div>;
}